import { parseAsArrayOf, parseAsString, parseAsStringEnum, useQueryStates } from 'nuqs';
import { useEffect } from 'react';
import { TranscriptionStatusFilter } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { CallTypeFilter } from '../../entities/LeadView/useLeadView';
import {
	CallLogUrlFilters,
	defaultCallLogFilters,
	EMPTY_CALL_LOG_FILTERS,
	hasAnyFilter,
	RecordingChannelsFilter,
	toLeadViewFilter,
} from './callLogFilters';

const callLogFilterParsers = {
	callType: parseAsStringEnum<CallTypeFilter>(Object.values(CallTypeFilter)),
	category: parseAsString,
	channels: parseAsStringEnum<RecordingChannelsFilter>(['1', '2']),
	from: parseAsString,
	manager: parseAsString,
	outcomes: parseAsArrayOf(parseAsString),
	role: parseAsString,
	sdr: parseAsString,
	tag: parseAsString,
	team: parseAsString,
	to: parseAsString,
	transcription: parseAsStringEnum<TranscriptionStatusFilter>(Object.values(TranscriptionStatusFilter)),
};

export function useCallLogFilters() {
	const userSession = useUserSession();
	const userId = userSession.user?.id;
	const [params, setParams] = useQueryStates(callLogFilterParsers, { history: 'replace' });

	const isSet = hasAnyFilter(params);
	// Built per use rather than memoized so the 7-day window follows the date if the page stays open overnight.
	const buildDefaults = (): CallLogUrlFilters => ({
		...EMPTY_CALL_LOG_FILTERS,
		...defaultCallLogFilters(userId, new Date()),
	});
	const defaults = buildDefaults();
	const effective: CallLogUrlFilters = isSet ? params : defaults;

	// A bare `/call-log` (nav bar, focused view) clears the params without remounting, so defaults reapply here.
	useEffect(() => {
		if (!isSet && userId) {
			setParams({ ...EMPTY_CALL_LOG_FILTERS, ...defaultCallLogFilters(userId, new Date()) });
		}
	}, [isSet, userId, setParams]);

	const setFilter = (next: Partial<CallLogUrlFilters>) => setParams(next);
	const removeFilter = (key: keyof CallLogUrlFilters) => setParams({ [key]: null });
	const clearAll = () => setParams(buildDefaults());
	const resetSecondary = () =>
		setParams({
			callType: null,
			category: null,
			channels: null,
			manager: null,
			role: null,
			tag: null,
			team: null,
			transcription: null,
		});

	const isDefault = (Object.keys(defaults) as (keyof CallLogUrlFilters)[]).every(
		key => JSON.stringify(effective[key] ?? null) === JSON.stringify(defaults[key] ?? null)
	);

	return {
		clearAll,
		isDefault,
		filter: toLeadViewFilter(effective),
		params: effective,
		removeFilter,
		resetSecondary,
		setFilter,
	};
}
